/*
 * Copyright (c) Hilo 2020.
 * Author: fharold
 *
 * hilo-oauth-manager
 * AuthenticationService.ts
 */

import Cookies from 'js-cookie';
import Unauthorized from "../model/exception/Unauthorized";
import {Listener, Service} from "./Service";
import {AccessToken} from "../model/auth/AccessToken";
import HTTPService from "./HTTPService";
import Config from "../../config";

export const KEY_COOKIE_ACCESS_TOKEN = 'access_token';
export const KEY_COOKIE_VERIFIER_CODE = 'code_verifier';
export const KEY_COOKIE_USER_ID = 'user_id';

const SCOPE = "USER,CLIENT"

/**
 * AuthenticationWatcher is used to monitor authentication.
 * authenticationWatcher(busy) is called when a network request is in progress or has just finished
 * authenticationRefreshed is called when a network request successfully
 */
export interface AuthenticationWatcher extends Listener<AccessToken> {
    authenticationRefreshed(token?: AccessToken): void;
}

export default class AuthenticationService extends Service<AccessToken, AuthenticationWatcher> {
    private _token?: AccessToken = Cookies.getJSON(KEY_COOKIE_ACCESS_TOKEN) as AccessToken;
    private _busy: boolean = false;
    public static id = "AuthenticationService";

    /**
     * start
     */
    public start() {
        super.start();
    }

    /**
     * stop
     */
    public stop() {
    }

    /**
     * initializeSession
     */
    public async initializeSession(httpService: HTTPService) {
        if (this.busy) return;

        this._setBusy(true);

        try {
            this._token = Cookies.getJSON(KEY_COOKIE_ACCESS_TOKEN) as AccessToken;
        } catch {
            console.error("failed to get token from cookies");
        }


        if (!this.token) {
            this._setBusy(false);
            console.error("token not found, skipping session initalization")
            throw Unauthorized;
        }

        try {
            await this.refreshAuth(httpService);
        } catch (e) {
            console.error("initializeSession:", e);
        } finally {
            this._setBusy(false);
        }
    }

    /**
     * notifyListeners
     */
    public notifyListeners = async () => {
        if (!this._token) {
            return;
        }

        for (let l of this.listeners) l.authenticationRefreshed(this._token)
    };


    /**
     * login
     */
    public login = async (httpService: HTTPService, authorizationCode: string, codeVerifier: string) => {
        const form = new FormData();

        form.append("grant_type", "code");
        form.append("client_id", Config.API_CLIENT_ID);
        form.append("client_secret", Config.API_CLIENT_SECRET);
        form.append("code", authorizationCode);
        form.append("code_verifier", codeVerifier);
        form.append("scope", SCOPE);

        try {
            const response = await httpService.default.post(`${Config.OAUTH_API_URL}/oauth/token`, form);
            const token = response.data as AccessToken;

            this.saveToken(token);

            this.notifyListeners();
        } catch (e) {
            console.error("failed to login :", e);
            throw new Error(e);
        }
    };

    /**
     * logout
     * @param shouldNotifyListeners Notify registered event listeners
     */
    public logout = async (shouldNotifyListeners: boolean = true) => {
        console.error("logout clearToken");
        this.clearToken();

        if (shouldNotifyListeners) {
            await this.notifyListeners();
        }

        window.location.reload();
    };

    /**
     * busy
     * @returns {boolean}
     */
    public get busy(): boolean {
        return this._busy;
    }

    /**
     * token
     * @returns {AccessToken}
     */
    public get token(): AccessToken | undefined {
        if (!this._token) {
            this._token = Cookies.getJSON(KEY_COOKIE_ACCESS_TOKEN) as AccessToken;
        }
        return this._token;
    }

    /**
     * _setBusy
     * @param state
     */
    private _setBusy(state: boolean) {
        this._busy = state;
        this.notifyListeners();
    }

    /**
     * refreshAuth
     * @param shouldNotifyListeners Notify registered event listeners
     * @param httpService
     * @return {Promise<void>}
     */
    public refreshAuth = async (httpService: HTTPService, shouldNotifyListeners: boolean = true) => {
        const form = new FormData();

        if (!this.token) {
            throw Unauthorized;
        }

        form.append("grant_type", "refresh_token");
        form.append("client_id", Config.API_CLIENT_ID);
        form.append("client_secret", Config.API_CLIENT_SECRET);
        form.append("refresh_token", this.token.refresh_token);
        form.append("scope", SCOPE);

        try {
            const response = await httpService.default.post(`${Config.OAUTH_API_URL}/oauth/token`, form);
            const token = response.data as AccessToken;

            this.saveToken(token);

            this.notifyListeners();
        } catch (e) {
            throw new Error(e);
        }
    };

    /**
     * saveToken
     */
    public saveToken = (token: AccessToken) => {
        if (!token) {
            this._token = undefined;
            Cookies.remove(KEY_COOKIE_ACCESS_TOKEN);
            return;
        }

        this._token = token;
        Cookies.set(KEY_COOKIE_ACCESS_TOKEN, token);
        console.info("Stored token in cookies.");
    };

    /**
     * clearToken
     */
    public clearToken = () => {
        Cookies.remove(KEY_COOKIE_ACCESS_TOKEN);
    };

    /**
     * saveCodeVerifier
     */
    public saveCodeVerifier = (codeVerifier: string) => {
        Cookies.set(KEY_COOKIE_VERIFIER_CODE, codeVerifier);
        console.info("Stored verifier code in cookies.");
    };

    /**
     * clearToken
     */
    get codeVerifier() {
        return Cookies.get(KEY_COOKIE_VERIFIER_CODE)
    };
}
